import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  Heading,
  Text,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Icon,
  Badge,
} from '@chakra-ui/react';
import { FiSave, FiEye, FiCode, FiLayout, FiZap, FiPlus, FiTrash } from 'react-icons/fi';
import AdminLayout from '../../layouts/AdminLayout';
import RichTextEditor from '../../components/common/RichTextEditor';
import AILoadingModal from '../../components/common/AILoadingModal';
import api from '../../services/api';
import { generateAboutAI } from '../../services/ai';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { getCategories, CategoryItem } from '../../services/categories';
import AIPromptInput from '../../components/common/AIPromptInput';
import { AITextModelId, getDefaultTextModelId } from '../../utils/aiModels';
import { processAIContent, cleanAIText } from '../../utils/aiTextProcessing';

type AboutPageData = {
  id?: number;
  title: string;
  subtitle: string;
  style: 'default' | 'modern' | 'timeline' | 'custom';
  content: string;
  sections: string;
  seo_title: string;
  seo_description: string;
};

const AboutAdminPage: React.FC = () => {
  const toast = useToast();
  const { data: settings } = usePublicSettings();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiProgress, setAiProgress] = useState<number | undefined>(undefined);
  const [aiStartTime, setAiStartTime] = useState<number | null>(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiAudience, setAiAudience] = useState('Fanoušci klubu');
  const [aiModelId, setAiModelId] = useState<AITextModelId>(getDefaultTextModelId());
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [foundationYear, setFoundationYear] = useState<string>('');
  const [timelineEvents, setTimelineEvents] = useState<Array<{ year: string; title: string }>>([
    { year: '', title: '' },
  ]);
  const [data, setData] = useState<AboutPageData>({
    title: '',
    subtitle: '',
    style: 'default',
    content: '',
    sections: '[]',
    seo_title: '',
    seo_description: '',
  });

  useEffect(() => {
    loadData();
    loadCategories();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/about');
      if (res.data) {
        setData(res.data);
      }
    } catch (e: any) {
      toast({ title: 'Chyba', description: 'Nepodařilo se načíst data', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await getCategories();
      setCategories(cats || []);
    } catch {}
  };

  const handleSave = async () => {
    if (!data.title.trim()) {
      toast({ title: 'Chyba', description: 'Vyplňte název stránky', status: 'warning' });
      return;
    }

    setSaving(true);
    try {
      await api.put('/admin/about', data);
      toast({ title: 'Uloženo', description: 'Stránka O klubu byla uložena', status: 'success' });
      await loadData();
    } catch (e: any) {
      toast({
        title: 'Chyba',
        description: e?.response?.data?.error || 'Nepodařilo se uložit',
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Opravdu smazat stránku O klubu?')) return;
    try {
      await api.delete('/admin/about');
      toast({ title: 'Smazáno', description: 'Stránka byla smazána', status: 'info' });
      setData({
        title: '',
        subtitle: '',
        style: 'default',
        content: '',
        sections: '[]',
        seo_title: '',
        seo_description: '',
      });
    } catch (e: any) {
      toast({ title: 'Chyba', description: 'Nepodařilo se smazat', status: 'error' });
    }
  };

  const handleGenerateAI = async () => {
    if (!aiPrompt.trim()) {
      toast({ title: 'Chyba', description: 'Zadejte poznámky pro AI', status: 'warning' });
      return;
    }

    setAiGenerating(true);
    setAiProgress(0);
    setAiStartTime(Date.now());

    // Start progress simulation
    const progressInterval = setInterval(() => {
      setAiProgress((prev) => {
        if (prev === undefined || prev >= 95) return prev;
        return Math.min(prev + Math.random() * 15 + 5, 95);
      });
    }, 2000);

    try {
      const clubName = settings?.club_name || '';
      const catsText = categories && categories.length
        ? `Rubriky (kategorie): ${categories.map((c) => `${c.name}${c.description ? ` – ${c.description}` : ''}`).join('; ')}`
        : '';
      const styleIntro = `Zvolený styl: ${data.style}.`;
      const timelineDetails = data.style === 'timeline'
        ? `\nČasová osa – dodatečné informace:\nRok založení: ${foundationYear || 'neuvedeno'}.\nKlíčové milníky (rok: událost):\n${timelineEvents
            .filter((e) => e.year.trim() || e.title.trim())
            .map((e) => `- ${e.year.trim() || '????'}: ${e.title.trim() || ''}`)
            .join('\n')}`
        : '';
      const extraGuidelines = data.style === 'timeline'
        ? 'Piš chronologicky, používej podnadpisy (h3) s rokem, pod nimi krátký odstavec. Kde se hodí, vlož seznamy (ul/li).' 
        : 'Rozděl text do sekcí s h2/h3 a odstavci. Kde se hodí, vlož seznamy (ul/li).';

      const fullPrompt = `${aiPrompt.trim()}\n\nInformace o klubu:\nNázev klubu: ${clubName || 'Fotbalový klub'}.\n${catsText}\n${styleIntro}${timelineDetails}\n\nPokyny pro výstup: ${extraGuidelines}`;

      const result = await generateAboutAI({
        prompt: fullPrompt,
        audience: aiAudience,
        style: data.style,
        club_name: clubName,
        model: aiModelId,
      });
      
      clearInterval(progressInterval);
      setAiProgress(100);
      
      setData((prev) => ({
        ...prev,
        content: processAIContent(result.html || prev.content, { cleanText: true, preserveHtmlTags: true }),
        title: cleanAIText(result.title || prev.title),
        subtitle: cleanAIText(result.subtitle || prev.subtitle),
        seo_title: cleanAIText(result.seo_title || prev.seo_title),
        seo_description: cleanAIText(result.seo_description || prev.seo_description),
      }));
      toast({
        title: 'Hotovo!',
        description: 'AI úspěšně vygenerovala obsah',
        status: 'success',
      });
    } catch (e: any) {
      clearInterval(progressInterval);
      console.error('AI generation error:', e);
      toast({
        title: 'Chyba AI',
        description: e?.response?.data?.error || 'Nepodařilo se vygenerovat obsah',
        status: 'error',
      });
    } finally {
      clearInterval(progressInterval);
      setTimeout(() => {
        setAiGenerating(false);
        setAiProgress(undefined);
        setAiStartTime(null);
      }, 1000); // Brief moment to show 100% completion
    }
  };

  const styleDescriptions: Record<string, { name: string; desc: string }> = {
    default: { name: 'Výchozí', desc: 'Jednoduchý layout s titulkem a obsahem' },
    modern: { name: 'Moderní', desc: 'Vizuálně atraktivní s hero obrázkem a sekcemi' },
    timeline: { name: 'Timeline', desc: 'Historie klubu na časové ose' },
    custom: { name: 'Vlastní HTML', desc: 'Plná kontrola nad obsahem a stylingem' },
  };

  return (
    <AdminLayout requireAdmin={false}>
      <Box>
        <HStack justify="space-between" mb={4}>
          <Heading size="lg">Stránka O klubu</Heading>
          <HStack>
            <Button
              leftIcon={<FiSave />}
              colorScheme="brand"
              onClick={handleSave}
              isLoading={saving}
            >
              Uložit
            </Button>
          </HStack>
        </HStack>

        <Text color="gray.600" mb={6}>
          Vytvořte nebo upravte stránku O klubu. Dostupná na <strong>/o-klubu</strong>.
        </Text>

        <Tabs variant="enclosed" colorScheme="brand">
          <TabList>
            <Tab><Icon as={FiLayout} mr={2} /> Obsah</Tab>
            <Tab><Icon as={FiCode} mr={2} /> Styl</Tab>
            <Tab><Icon as={FiEye} mr={2} /> SEO</Tab>
          </TabList>

          <TabPanels>
            {/* Content Tab */}
            <TabPanel>
              <VStack align="stretch" spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Název stránky</FormLabel>
                  <Input
                    value={data.title}
                    onChange={(e) => setData((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="O naší klubu"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Podtitulek</FormLabel>
                  <Input
                    value={data.subtitle}
                    onChange={(e) => setData((prev) => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="Naše historie, hodnoty a komunita"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Styl stránky</FormLabel>
                  <Select
                    value={data.style}
                    onChange={(e) =>
                      setData((prev) => ({ ...prev, style: e.target.value as any }))
                    }
                  >
                    {Object.entries(styleDescriptions).map(([key, { name }]) => (
                      <option key={key} value={key}>
                        {name}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="sm" color="gray.600" mt={2}>
                    {styleDescriptions[data.style]?.desc}
                  </Text>
                </FormControl>

                {data.style === 'timeline' && (
                  <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                    <Heading size="sm" mb={3}>Časová osa – podklady</Heading>
                    <VStack align="stretch" spacing={3}>
                      <FormControl>
                        <FormLabel>Rok založení</FormLabel>
                        <Input
                          type="text"
                          value={foundationYear}
                          onChange={(e) => setFoundationYear(e.target.value)}
                          placeholder="např. 1932"
                        />
                      </FormControl>
                      <Box>
                        <HStack justify="space-between" mb={2}>
                          <Text fontWeight="semibold">Klíčové milníky</Text>
                          <Button size="sm" leftIcon={<FiPlus />} onClick={() => setTimelineEvents((prev) => [...prev, { year: '', title: '' }])}>Přidat milník</Button>
                        </HStack>
                        <VStack align="stretch" spacing={2}>
                          {timelineEvents.map((ev, idx) => (
                            <HStack key={idx} spacing={2} align="stretch">
                              <Input
                                placeholder="Rok"
                                value={ev.year}
                                onChange={(e) => setTimelineEvents((prev) => prev.map((it, i) => i === idx ? { ...it, year: e.target.value } : it))}
                                maxW="120px"
                              />
                              <Input
                                placeholder="Událost / popis"
                                value={ev.title}
                                onChange={(e) => setTimelineEvents((prev) => prev.map((it, i) => i === idx ? { ...it, title: e.target.value } : it))}
                              />
                              <Button aria-label="Odebrat" size="sm" colorScheme="red" variant="outline" onClick={() => setTimelineEvents((prev) => prev.filter((_, i) => i !== idx))}>
                                <Icon as={FiTrash} />
                              </Button>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    </VStack>
                  </Box>
                )}

                {/* Hero image removed: About page uses club logo and name from settings */}

                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  bg="gray.50"
                >
                  <HStack justify="space-between" mb={3} align="flex-start">
                    <Box>
                      <Heading size="sm" mb={1}>AI generátor obsahu</Heading>
                      <Text fontSize="sm" color="gray.600">
                        Napište krátké poznámky (např. historie, úspěchy, hodnoty) a nechte AI připravit návrh stránky.
                      </Text>
                    </Box>
                    <Button
                      leftIcon={<FiZap />}
                      colorScheme="gray"
                      variant="solid"
                      onClick={handleGenerateAI}
                      isDisabled={aiGenerating}
                    >
                      Vygenerovat
                    </Button>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <FormControl>
                      <FormLabel>Poznámky pro AI</FormLabel>
                      <AIPromptInput
                        value={aiPrompt}
                        onChange={setAiPrompt}
                        onSubmit={(_, modelId) => {
                          setAiModelId(modelId);
                          return handleGenerateAI();
                        }}
                        isSubmitting={aiGenerating}
                        placeholder="Popište historii klubu, současné cíle, přístup k mládeži..."
                        helperText="AI připraví návrh stránky O klubu na základě těchto poznámek."
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Cílové publikum</FormLabel>
                      <Input
                        value={aiAudience}
                        onChange={(e) => setAiAudience(e.target.value)}
                        placeholder="Fanoušci klubu"
                      />
                    </FormControl>
                  </VStack>
                </Box>

                <FormControl>
                  <FormLabel>Obsah stránky</FormLabel>
                  <RichTextEditor
                    value={data.content}
                    onChange={(value) => setData((prev) => ({ ...prev, content: value }))}
                    height="400px"
                    toolbar="full"
                    showImageResize={true}
                    placeholder="Zadejte obsah stránky O nás..."
                  />
                </FormControl>
              </VStack>
            </TabPanel>

            {/* Style Tab */}
            <TabPanel>
              <VStack align="stretch" spacing={4}>
                <FormControl>
                  <FormLabel>Styl stránky</FormLabel>
                  <Select
                    value={data.style}
                    onChange={(e) =>
                      setData((prev) => ({ ...prev, style: e.target.value as any }))
                    }
                  >
                    {Object.entries(styleDescriptions).map(([key, { name, desc }]) => (
                      <option key={key} value={key}>
                        {name}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="sm" color="gray.600" mt={2}>
                    {styleDescriptions[data.style]?.desc}
                  </Text>
                </FormControl>

                <Box
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="blue.50"
                  borderColor="blue.200"
                >
                  <Heading size="sm" mb={2}>Vybraný styl: {styleDescriptions[data.style]?.name}</Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={3}>
                    <Box>
                      <Badge colorScheme="blue" mb={1}>Výchozí</Badge>
                      <Text fontSize="sm">Jednoduchý layout, ideální pro text</Text>
                    </Box>
                    <Box>
                      <Badge colorScheme="purple" mb={1}>Moderní</Badge>
                      <Text fontSize="sm">Hero obrázek + sekce karet</Text>
                    </Box>
                    <Box>
                      <Badge colorScheme="green" mb={1}>Timeline</Badge>
                      <Text fontSize="sm">Historie klubu chronologicky</Text>
                    </Box>
                    <Box>
                      <Badge colorScheme="orange" mb={1}>Vlastní</Badge>
                      <Text fontSize="sm">Plná kontrola přes HTML/CSS</Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                {data.style === 'custom' && (
                  <Box
                    p={3}
                    bg="yellow.50"
                    borderWidth="1px"
                    borderColor="yellow.300"
                    borderRadius="md"
                  >
                    <Text fontSize="sm" fontWeight="semibold" mb={1}>
                      Vlastní HTML režim
                    </Text>
                    <Text fontSize="sm">
                      V editoru můžete použít jakékoliv HTML a CSS. Ujistěte se, že je kód validní.
                    </Text>
                  </Box>
                )}
              </VStack>
            </TabPanel>

            {/* SEO Tab */}
            <TabPanel>
              <VStack align="stretch" spacing={4}>
                <FormControl>
                  <FormLabel>SEO Titulek</FormLabel>
                  <Input
                    value={data.seo_title}
                    onChange={(e) => setData((prev) => ({ ...prev, seo_title: e.target.value }))}
                    placeholder="Pokud prázdné, použije se název stránky"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>SEO Popis</FormLabel>
                  <Textarea
                    value={data.seo_description}
                    onChange={(e) =>
                      setData((prev) => ({ ...prev, seo_description: e.target.value }))
                    }
                    placeholder="Krátký popis pro vyhledávače (doporučeno 150-160 znaků)"
                    rows={4}
                  />
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Délka: {data.seo_description.length} znaků
                  </Text>
                </FormControl>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <Box mt={6} pt={4} borderTopWidth="1px">
          <HStack justify="space-between">
            <Button colorScheme="red" variant="outline" onClick={handleDelete}>
              Smazat stránku
            </Button>
            <HStack>
              <Button as="a" href="/o-klubu" target="_blank" variant="outline">
                Náhled
              </Button>
              <Button
                leftIcon={<FiSave />}
                colorScheme="brand"
                onClick={handleSave}
                isLoading={saving}
              >
                Uložit změny
              </Button>
            </HStack>
          </HStack>
        </Box>
      </Box>
      
      <AILoadingModal
        isOpen={aiGenerating}
        onClose={() => {}} // Can't close while AI is working
        title="AI generuje obsah"
        message="AI pracuje na generování obsahu pro stránku O klubu..."
        progress={aiProgress}
        estimatedTime={aiStartTime ? 30 : undefined} // 30 seconds estimated
      />
    </AdminLayout>
  );
};

export default AboutAdminPage;
